<script lang="ts">
export default {
  name: 'BusinessHourPreviewModal',
}
</script>
<script setup lang="ts" >
import { useI18n } from 'vue-i18n'
import { KkModal, KkIcon, KkButton } from '../../../../../../components/base'
const { t } = useI18n()

interface FoodOption {
  lastOrderTime?: string
  kind?: string
}
interface Period {
  openTime: string
  closeTime: string
  foodOption?: FoodOption
}
interface BusinessHour {
  periods: Period[]
  wday: string
  isNotSet? : boolean
}

interface WdayIndexRegularHour {
  periods: Period[]
  indexes: number[]
  wdays: string[]
  isNotSet? : boolean
}

interface AdditionalDescription {
  context: string
}

interface BusinessHourRequestBody {
  regularHours: BusinessHour[]
  holidayRegularHour?: BusinessHour
  dayBeforeHolidayRegularHour?: BusinessHour
  wdayIndexRegularHours: WdayIndexRegularHour[]
  regularHourAdditionalDescription?: AdditionalDescription
  regularHolidayAdditionalDescription?: AdditionalDescription
  specialHours: BusinessHour[]
  moreHours: BusinessHour[]
  synced: boolean
}

interface Props {
  isVisible: boolean
  businessHourSynced: boolean
  showSubmit?: boolean
  title: string
  linkedSites: string[]
  businessHourRequestBody: BusinessHourRequestBody
  enabledBusinessHoursCrawler: boolean
}
const siteTypes = ['google', 'tabelog', 'gnavi', 'hotPepperGourmet', 'retty', 'docomo', 'tripAdvisor', 'yahooPlace'] as const
type SiteType = typeof siteTypes[number]
const props = withDefaults(defineProps<Props>(), {
  isVisible: false,
  showSubmit: true,
  title: '',
  linkedSites: () => [],
  enabledBusinessHoursCrawler: false
})
const emit = defineEmits(['submit', 'close'])
const sites = siteTypes.filter(type => props.linkedSites.includes(type))

interface Week { [type: string]: string }
interface Kind { [type: string]: string }
const week: Week = { sunday: '日曜日', monday: '月曜日', tuesday: '火曜日', wednesday: '水曜日', thursday: '木曜日', friday: '金曜日', saturday: '土曜日' }
const kind: Kind = { morning: 'モーニング', lunch: 'ランチ', dinner: 'ディナー' }
const daysOfWeek: string[] = [ 'sunday','monday','tuesday','wednesday','thursday','friday','saturday']

const detailText = (site: SiteType) => {
  if (site === 'google' && props.enabledBusinessHoursCrawler) return t('pages.CompaniesLocationStoreRegularHoursEdit.preview.enabledBusinessHoursCrawler')
  const regularHours = props.businessHourRequestBody.regularHours
  const holidayRegularHour = props.businessHourRequestBody.holidayRegularHour
  const dayBeforeHolidayRegularHour = props.businessHourRequestBody.dayBeforeHolidayRegularHour
  const wdayIndexRegularHours = props.businessHourRequestBody.wdayIndexRegularHours
  const regularHourAdditionalDescription = props.businessHourRequestBody.regularHourAdditionalDescription
  const regularHolidayAdditionalDescription = props.businessHourRequestBody.regularHolidayAdditionalDescription

  let detailBusinessHourText = ''
  let detailHolidayHourText = ''
  let googleBusinessHourText = ''
  if (site !== 'google') {
    detailBusinessHourText += `<b>${t('models.storeRegularHour.modelName')}</b>\n`
    detailHolidayHourText += `<b>${t('models.storeRegularHour.regularHoliday')}</b>\n`
  }
  // 曜日別の営業時間
  if (!props.enabledBusinessHoursCrawler) {
    for (let regularHour of regularHours) {
      if (!regularHour.periods.length) {
        detailHolidayHourText += `${week[regularHour.wday]} ${t('models.storeRegularHour.regularHoliday')}\n`
        googleBusinessHourText += `${week[regularHour.wday]} ${t('models.storeRegularHour.regularHoliday')}\n`
        continue
      } else {
        detailBusinessHourText += `${week[regularHour.wday]} `
        googleBusinessHourText += `${week[regularHour.wday]} `
        for (let i = 0; i < regularHour.periods.length; i++) {
          if (site === 'yahooPlace' && i > 2) break
          const period = regularHour.periods[i]
          detailBusinessHourText += makePeriodText(period, site)
          googleBusinessHourText += makePeriodText(period, site)
        }
        googleBusinessHourText += '\n'
        detailBusinessHourText += '\n'
      }
    }
  }

  if (site === 'tripAdvisor') return detailBusinessHourText
  // 祝日の営業時間
  if (holidayRegularHour && !holidayRegularHour.isNotSet) {
    if (!holidayRegularHour.periods.length) {
      detailHolidayHourText += `${t('labels.holiday')} ${t('models.storeRegularHour.regularHoliday')}\n`
    } else {
      detailBusinessHourText += `${t('labels.holiday')} `
      for (let period of holidayRegularHour.periods) {
        detailBusinessHourText += makePeriodText(period, site)
      }
      detailBusinessHourText += '\n'
    }
  }
  // 祝前日の営業時間
  if (dayBeforeHolidayRegularHour && !dayBeforeHolidayRegularHour.isNotSet) {
    if (!dayBeforeHolidayRegularHour.periods.length) {
      detailHolidayHourText += `${t('labels.dayBeforeHoliday')} ${t('models.storeRegularHour.regularHoliday')}\n`
    } else {
      detailBusinessHourText += `${t('labels.dayBeforeHoliday')} `
      for (let period of dayBeforeHolidayRegularHour.periods) {
        detailBusinessHourText += makePeriodText(period, site)
      }
      detailBusinessHourText += '\n'
    }
  }
  if (site !== 'yahooPlace') {
    // 週と曜日の組み合わせ
    for (let wdayIndexRegularHour of wdayIndexRegularHours) {
      if (wdayIndexRegularHour.isNotSet) continue
      // wdays, indexesを昇順にソート
      wdayIndexRegularHour.indexes.sort((a: number, b: number): number => a - b)
      wdayIndexRegularHour.wdays.sort((a: string, b: string): number => daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b))
      for (let index of wdayIndexRegularHour.indexes) for (let wday of wdayIndexRegularHour.wdays) {
        const wdayIndex = `第${index}${week[wday]}`
        if (!wdayIndexRegularHour.periods.length) {
          detailHolidayHourText += `${wdayIndex} ${t('models.storeRegularHour.regularHoliday')}\n`
        } else {
          detailBusinessHourText += `${wdayIndex} `
          for (let period of wdayIndexRegularHour.periods) {
            detailBusinessHourText += makePeriodText(period, site)
          }
          detailBusinessHourText += '\n'
        }
      }
    }
  }
  // 営業時間の補足
  if (regularHourAdditionalDescription && regularHourAdditionalDescription.context) detailBusinessHourText += `${regularHourAdditionalDescription.context}\n`
  if (site !== 'yahooPlace') {
    if (regularHolidayAdditionalDescription && regularHolidayAdditionalDescription.context) detailHolidayHourText += `${regularHolidayAdditionalDescription.context}`
  }

  if (site === 'google') return googleBusinessHourText
  else return `${detailBusinessHourText}\n${detailHolidayHourText}`
}
const makePeriodText = (period: Period, site: SiteType) => {
  if (period.openTime == '00:00' && period.closeTime == '24:00') {
    return t('models.storeRegularHour.open24hours')
  }
  let periodText = `${period.openTime} - ${period.closeTime} `
  if (!['google', 'yahooPlace'].includes(site)) periodText += ` ${period.foodOption?.lastOrderTime ? `(LO ${period.foodOption.lastOrderTime})` : ''} ${period.foodOption?.kind ? kind[period.foodOption.kind] : ''} `
  return periodText
}

</script>

<template>
  <KkModal :is-visible="props.isVisible" width="1076px" @change="emit('close')">
    <div class="p-modal__content__header">
      {{ props.title }}
    </div>
    <div class="p-modal__content__body">
      <div class="p-table">
        <div class="p-table__content">
          <table class="businessHour">
            <thead>
              <tr>
                <th class="businessHour__siteSection">{{ t('pages.CompaniesLocationStoreRegularHoursEdit.preview.site') }}</th>
                <th>{{ t('pages.CompaniesLocationStoreRegularHoursEdit.preview.detail') }}</th>
              </tr>
              <tr class="p-table__content__space">
                <td colspan="4" />
              </tr>
            </thead>
            <tbody>
              <template v-for="site in sites" :key="site">
                <tr>
                  <td class="businessHour__site" :rowSpan="1">
                    <span class="businessHour__section">
                      {{ t(`labels.sites.${site}`) }}
                      <KkIcon v-if="props.businessHourSynced && site == 'google'" class="businessHour__icon" name="protection" color="#79acb0" />
                    </span>
                  </td>
                  <td class="businessHour__detailText">
                    <span v-html="detailText(site)" />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="p-modal__content__footer">
      <div class="c-button c-button--right">
        <KkButton
          class="footerCancelBtn"
          color="secondary"
          outlined
          :text="t('labels.close')"
          @click="emit('close')"
        />
        <KkButton
          v-if="props.showSubmit"
          color="primary"
          outlined
          :text="t('labels.confirm')"
          @click="emit('submit')"
        />
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
@import '../../../../../../../../stylesheets/biz_spa/foundation/_var.scss';
.businessHour {
  height: 100%;
  &__siteSection {
    width: 25%;
  }
  &__section {
    display: flex;
    align-items: center;
    background-color: #f8f9fa;
    height: 100%;
  }
  &__site {
    background-color: #f8f9fa;
  }
  &__icon {
    width: 12px;
    margin-left: 5px;
  }
  td:empty {
    display: table-cell;
  }
  &__detailText {
    white-space: pre-wrap;
  }
}

.footerCancelBtn {
  margin-right: 8px;
}
</style>
